import type { Building } from './types';

// === MEDIEVAL ERA — prod ×3, cost ×2.5 ===

export const BUILDINGS_MEDIEVAL: readonly Building[] = [
  // Gold
  {
    id: 'med_forge', name: 'Forge', description: "Fabrique des métaux précieux",
    resource: 'gold',
    baseCost: { gold: 25, food: 0, faith: 0, knowledge: 0 },
    baseProduction: 3, costMultiplier: 1.15, level: 0, maxLevel: 100,
  },
  {
    id: 'med_guild_hall', name: 'Halle de Guilde', description: "Commerce organisé par les guildes",
    resource: 'gold',
    baseCost: { gold: 250, food: 125, faith: 0, knowledge: 0 },
    baseProduction: 15, costMultiplier: 1.18, level: 0, maxLevel: 100,
  },
  {
    id: 'med_royal_mint', name: 'Hôtel Royal de la Monnaie', description: "Frappe les pièces royales",
    resource: 'gold',
    baseCost: { gold: 2500, food: 500, faith: 250, knowledge: 0 },
    baseProduction: 75, costMultiplier: 1.20, level: 0, maxLevel: 100,
  },
  // Food
  {
    id: 'med_vineyard', name: 'Vignoble', description: "Cultive la vigne et produit du vin",
    resource: 'food',
    baseCost: { gold: 25, food: 0, faith: 0, knowledge: 0 },
    baseProduction: 3, costMultiplier: 1.15, level: 0, maxLevel: 100,
  },
  {
    id: 'med_windmill', name: 'Moulin à Vent', description: "Broie le grain avec le vent",
    resource: 'food',
    baseCost: { gold: 125, food: 250, faith: 0, knowledge: 0 },
    baseProduction: 15, costMultiplier: 1.18, level: 0, maxLevel: 100,
  },
  {
    id: 'med_storehouse', name: 'Entrepôt', description: "Stocke d'importantes quantités de vivres",
    resource: 'food',
    baseCost: { gold: 500, food: 2500, faith: 0, knowledge: 250 },
    baseProduction: 75, costMultiplier: 1.20, level: 0, maxLevel: 100,
  },
  // Faith
  {
    id: 'med_chapel', name: 'Chapelle', description: "Petite maison de prière",
    resource: 'faith',
    baseCost: { gold: 38, food: 25, faith: 0, knowledge: 0 },
    baseProduction: 3, costMultiplier: 1.15, level: 0, maxLevel: 100,
  },
  {
    id: 'med_monastery', name: 'Monastère', description: "Communauté dédiée à la foi",
    resource: 'faith',
    baseCost: { gold: 250, food: 250, faith: 250, knowledge: 0 },
    baseProduction: 15, costMultiplier: 1.18, level: 0, maxLevel: 100,
  },
  {
    id: 'med_cathedral', name: 'Cathédrale', description: "Majestueux lieu de culte",
    resource: 'faith',
    baseCost: { gold: 1250, food: 750, faith: 2500, knowledge: 500 },
    baseProduction: 75, costMultiplier: 1.20, level: 0, maxLevel: 100,
  },
  // Knowledge
  {
    id: 'med_scriptorium', name: 'Scriptorium', description: "Copie et préserve les manuscrits",
    resource: 'knowledge',
    baseCost: { gold: 50, food: 38, faith: 0, knowledge: 0 },
    baseProduction: 3, costMultiplier: 1.15, level: 0, maxLevel: 100,
  },
  {
    id: 'med_university', name: 'Université', description: "Enseignement supérieur médiéval",
    resource: 'knowledge',
    baseCost: { gold: 375, food: 250, faith: 125, knowledge: 250 },
    baseProduction: 15, costMultiplier: 1.18, level: 0, maxLevel: 100,
  },
  {
    id: 'med_observatory', name: 'Observatoire', description: "Scrute les étoiles pour le savoir",
    resource: 'knowledge',
    baseCost: { gold: 1250, food: 750, faith: 500, knowledge: 2500 },
    baseProduction: 75, costMultiplier: 1.20, level: 0, maxLevel: 100,
  },
];

// === RENAISSANCE ERA (modern) — prod ×8, cost ×6 ===

export const BUILDINGS_MODERN: readonly Building[] = [
  // Gold
  {
    id: 'mod_bank', name: 'Banque', description: "Établissement financier de la Renaissance",
    resource: 'gold',
    baseCost: { gold: 60, food: 0, faith: 0, knowledge: 0 },
    baseProduction: 8, costMultiplier: 1.15, level: 0, maxLevel: 100,
  },
  {
    id: 'mod_trading_post', name: 'Comptoir Commercial', description: "Hub du commerce international",
    resource: 'gold',
    baseCost: { gold: 600, food: 300, faith: 0, knowledge: 0 },
    baseProduction: 40, costMultiplier: 1.18, level: 0, maxLevel: 100,
  },
  {
    id: 'mod_merchant_fleet', name: 'Flotte Marchande', description: "Navires transportant richesses des mers",
    resource: 'gold',
    baseCost: { gold: 6000, food: 1200, faith: 600, knowledge: 0 },
    baseProduction: 200, costMultiplier: 1.20, level: 0, maxLevel: 100,
  },
  // Food
  {
    id: 'mod_plantation', name: 'Plantation', description: "Grande culture agricole coloniale",
    resource: 'food',
    baseCost: { gold: 60, food: 0, faith: 0, knowledge: 0 },
    baseProduction: 8, costMultiplier: 1.15, level: 0, maxLevel: 100,
  },
  {
    id: 'mod_greenhouse', name: 'Serre', description: "Culture protégée des intempéries",
    resource: 'food',
    baseCost: { gold: 300, food: 600, faith: 0, knowledge: 0 },
    baseProduction: 40, costMultiplier: 1.18, level: 0, maxLevel: 100,
  },
  {
    id: 'mod_granary_royal', name: 'Grenier Royal', description: "Réserves alimentaires royales",
    resource: 'food',
    baseCost: { gold: 1200, food: 6000, faith: 0, knowledge: 600 },
    baseProduction: 200, costMultiplier: 1.20, level: 0, maxLevel: 100,
  },
  // Faith
  {
    id: 'mod_basilica', name: 'Basilique', description: "Grande église de la Renaissance",
    resource: 'faith',
    baseCost: { gold: 90, food: 60, faith: 0, knowledge: 0 },
    baseProduction: 8, costMultiplier: 1.15, level: 0, maxLevel: 100,
  },
  {
    id: 'mod_seminary', name: 'Séminaire', description: "Formation théologique avancée",
    resource: 'faith',
    baseCost: { gold: 600, food: 600, faith: 600, knowledge: 0 },
    baseProduction: 40, costMultiplier: 1.18, level: 0, maxLevel: 100,
  },
  {
    id: 'mod_vatican_lib', name: 'Bibliothèque Vaticane', description: "Trésor de la connaissance sacrée",
    resource: 'faith',
    baseCost: { gold: 3000, food: 1800, faith: 6000, knowledge: 1200 },
    baseProduction: 200, costMultiplier: 1.20, level: 0, maxLevel: 100,
  },
  // Knowledge
  {
    id: 'mod_laboratory', name: 'Laboratoire', description: "Expériences scientifiques de pointe",
    resource: 'knowledge',
    baseCost: { gold: 120, food: 90, faith: 0, knowledge: 0 },
    baseProduction: 8, costMultiplier: 1.15, level: 0, maxLevel: 100,
  },
  {
    id: 'mod_printing_press', name: "Imprimerie", description: "Diffuse le savoir à grande échelle",
    resource: 'knowledge',
    baseCost: { gold: 900, food: 600, faith: 300, knowledge: 600 },
    baseProduction: 40, costMultiplier: 1.18, level: 0, maxLevel: 100,
  },
  {
    id: 'mod_academy_sci', name: 'Académie des Sciences', description: "Sommet du savoir de la Renaissance",
    resource: 'knowledge',
    baseCost: { gold: 3000, food: 1800, faith: 1200, knowledge: 6000 },
    baseProduction: 200, costMultiplier: 1.20, level: 0, maxLevel: 100,
  },
];

// === INDUSTRIAL ERA — prod ×20, cost ×15 ===

export const BUILDINGS_INDUSTRIAL: readonly Building[] = [
  // Gold
  {
    id: 'ind_factory', name: 'Usine', description: "Production industrielle de masse",
    resource: 'gold',
    baseCost: { gold: 150, food: 0, faith: 0, knowledge: 0 },
    baseProduction: 20, costMultiplier: 1.15, level: 0, maxLevel: 100,
  },
  {
    id: 'ind_stock_exchange', name: 'Bourse', description: "Marché financier industriel",
    resource: 'gold',
    baseCost: { gold: 1500, food: 750, faith: 0, knowledge: 0 },
    baseProduction: 100, costMultiplier: 1.18, level: 0, maxLevel: 100,
  },
  {
    id: 'ind_central_bank', name: 'Banque Centrale', description: "Pilier du système monétaire national",
    resource: 'gold',
    baseCost: { gold: 15000, food: 3000, faith: 1500, knowledge: 0 },
    baseProduction: 500, costMultiplier: 1.20, level: 0, maxLevel: 100,
  },
  // Food
  {
    id: 'ind_cannery', name: 'Conserverie', description: "Conserve les aliments à grande échelle",
    resource: 'food',
    baseCost: { gold: 150, food: 0, faith: 0, knowledge: 0 },
    baseProduction: 20, costMultiplier: 1.15, level: 0, maxLevel: 100,
  },
  {
    id: 'ind_refrigeration', name: 'Réfrigération', description: "Conservation par le froid industriel",
    resource: 'food',
    baseCost: { gold: 750, food: 1500, faith: 0, knowledge: 0 },
    baseProduction: 100, costMultiplier: 1.18, level: 0, maxLevel: 100,
  },
  {
    id: 'ind_agri_corp', name: 'Agro-Corporation', description: "Agriculture industrielle de masse",
    resource: 'food',
    baseCost: { gold: 3000, food: 15000, faith: 0, knowledge: 1500 },
    baseProduction: 500, costMultiplier: 1.20, level: 0, maxLevel: 100,
  },
  // Faith
  {
    id: 'ind_revival_hall', name: "Salle de Réveil", description: "Mouvement évangélique industriel",
    resource: 'faith',
    baseCost: { gold: 225, food: 150, faith: 0, knowledge: 0 },
    baseProduction: 20, costMultiplier: 1.15, level: 0, maxLevel: 100,
  },
  {
    id: 'ind_mission', name: 'Mission', description: "Évangélisation des territoires industriels",
    resource: 'faith',
    baseCost: { gold: 1500, food: 1500, faith: 1500, knowledge: 0 },
    baseProduction: 100, costMultiplier: 1.18, level: 0, maxLevel: 100,
  },
  {
    id: 'ind_grand_temple', name: 'Grand Temple', description: "Monument spirituel de l'ère industrielle",
    resource: 'faith',
    baseCost: { gold: 7500, food: 4500, faith: 15000, knowledge: 3000 },
    baseProduction: 500, costMultiplier: 1.20, level: 0, maxLevel: 100,
  },
  // Knowledge
  {
    id: 'ind_research_lab', name: 'Laboratoire de Recherche', description: "Recherche industrielle appliquée",
    resource: 'knowledge',
    baseCost: { gold: 300, food: 225, faith: 0, knowledge: 0 },
    baseProduction: 20, costMultiplier: 1.15, level: 0, maxLevel: 100,
  },
  {
    id: 'ind_telegraph', name: 'Télégraphe', description: "Réseau de communication révolutionnaire",
    resource: 'knowledge',
    baseCost: { gold: 2250, food: 1500, faith: 750, knowledge: 1500 },
    baseProduction: 100, costMultiplier: 1.18, level: 0, maxLevel: 100,
  },
  {
    id: 'ind_patent_bureau', name: 'Bureau des Brevets', description: "Protège et valorise les inventions",
    resource: 'knowledge',
    baseCost: { gold: 7500, food: 4500, faith: 3000, knowledge: 15000 },
    baseProduction: 500, costMultiplier: 1.20, level: 0, maxLevel: 100,
  },
];

// === CONTEMPORARY ERA — prod ×50, cost ×35 ===

export const BUILDINGS_CONTEMPORARY: readonly Building[] = [
  // Gold
  {
    id: 'con_startup', name: 'Startup', description: "Entreprise tech à croissance rapide",
    resource: 'gold',
    baseCost: { gold: 350, food: 0, faith: 0, knowledge: 0 },
    baseProduction: 50, costMultiplier: 1.15, level: 0, maxLevel: 100,
  },
  {
    id: 'con_hedge_fund', name: 'Hedge Fund', description: "Fonds spéculatif à haute rentabilité",
    resource: 'gold',
    baseCost: { gold: 3500, food: 1750, faith: 0, knowledge: 0 },
    baseProduction: 250, costMultiplier: 1.18, level: 0, maxLevel: 100,
  },
  {
    id: 'con_crypto_mine', name: 'Mine Crypto', description: "Minage de cryptomonnaies",
    resource: 'gold',
    baseCost: { gold: 35000, food: 7000, faith: 3500, knowledge: 0 },
    baseProduction: 1250, costMultiplier: 1.20, level: 0, maxLevel: 100,
  },
  // Food
  {
    id: 'con_vertical_farm', name: 'Ferme Verticale', description: "Agriculture urbaine en hauteur",
    resource: 'food',
    baseCost: { gold: 350, food: 0, faith: 0, knowledge: 0 },
    baseProduction: 50, costMultiplier: 1.15, level: 0, maxLevel: 100,
  },
  {
    id: 'con_food_lab', name: 'Laboratoire Alimentaire', description: "Innovation dans la nourriture synthétique",
    resource: 'food',
    baseCost: { gold: 1750, food: 3500, faith: 0, knowledge: 0 },
    baseProduction: 250, costMultiplier: 1.18, level: 0, maxLevel: 100,
  },
  {
    id: 'con_global_logistics', name: 'Logistique Mondiale', description: "Distribution alimentaire planétaire",
    resource: 'food',
    baseCost: { gold: 7000, food: 35000, faith: 0, knowledge: 3500 },
    baseProduction: 1250, costMultiplier: 1.20, level: 0, maxLevel: 100,
  },
  // Faith
  {
    id: 'con_megachurch', name: 'Méga-Église', description: "Lieu de culte à grande capacité",
    resource: 'faith',
    baseCost: { gold: 525, food: 350, faith: 0, knowledge: 0 },
    baseProduction: 50, costMultiplier: 1.15, level: 0, maxLevel: 100,
  },
  {
    id: 'con_meditation_app', name: 'App de Méditation', description: "Spiritualité numérique de masse",
    resource: 'faith',
    baseCost: { gold: 3500, food: 3500, faith: 3500, knowledge: 0 },
    baseProduction: 250, costMultiplier: 1.18, level: 0, maxLevel: 100,
  },
  {
    id: 'con_spiritual_retreat', name: 'Retraite Spirituelle', description: "Sanctuaire de méditation et de foi",
    resource: 'faith',
    baseCost: { gold: 17500, food: 10500, faith: 35000, knowledge: 7000 },
    baseProduction: 1250, costMultiplier: 1.20, level: 0, maxLevel: 100,
  },
  // Knowledge
  {
    id: 'con_data_center', name: 'Centre de Données', description: "Infrastructure informatique massive",
    resource: 'knowledge',
    baseCost: { gold: 700, food: 525, faith: 0, knowledge: 0 },
    baseProduction: 50, costMultiplier: 1.15, level: 0, maxLevel: 100,
  },
  {
    id: 'con_ai_lab', name: 'Laboratoire IA', description: "Recherche en intelligence artificielle",
    resource: 'knowledge',
    baseCost: { gold: 5250, food: 3500, faith: 1750, knowledge: 3500 },
    baseProduction: 250, costMultiplier: 1.18, level: 0, maxLevel: 100,
  },
  {
    id: 'con_space_agency', name: "Agence Spatiale", description: "Exploration et recherche spatiale",
    resource: 'knowledge',
    baseCost: { gold: 17500, food: 10500, faith: 7000, knowledge: 35000 },
    baseProduction: 1250, costMultiplier: 1.20, level: 0, maxLevel: 100,
  },
];

// === FUTURE ERA — prod ×150, cost ×100 ===

export const BUILDINGS_FUTURE: readonly Building[] = [
  // Gold
  {
    id: 'fut_quantum_trader', name: 'Trader Quantique', description: "Transactions à vitesse quantique",
    resource: 'gold',
    baseCost: { gold: 1000, food: 0, faith: 0, knowledge: 0 },
    baseProduction: 150, costMultiplier: 1.15, level: 0, maxLevel: 100,
  },
  {
    id: 'fut_dyson_collector', name: 'Collecteur Dyson', description: "Capture l'énergie d'une étoile entière",
    resource: 'gold',
    baseCost: { gold: 10000, food: 5000, faith: 0, knowledge: 0 },
    baseProduction: 750, costMultiplier: 1.18, level: 0, maxLevel: 100,
  },
  {
    id: 'fut_matter_printer', name: 'Imprimante à Matière', description: "Fabrique de l'or ex nihilo",
    resource: 'gold',
    baseCost: { gold: 100000, food: 20000, faith: 10000, knowledge: 0 },
    baseProduction: 3750, costMultiplier: 1.20, level: 0, maxLevel: 100,
  },
  // Food
  {
    id: 'fut_bio_synthesizer', name: 'Bio-Synthétiseur', description: "Synthèse biologique des nutriments",
    resource: 'food',
    baseCost: { gold: 1000, food: 0, faith: 0, knowledge: 0 },
    baseProduction: 150, costMultiplier: 1.15, level: 0, maxLevel: 100,
  },
  {
    id: 'fut_nano_farm', name: 'Nano-Ferme', description: "Agriculture nanotechnologique",
    resource: 'food',
    baseCost: { gold: 5000, food: 10000, faith: 0, knowledge: 0 },
    baseProduction: 750, costMultiplier: 1.18, level: 0, maxLevel: 100,
  },
  {
    id: 'fut_orbital_garden', name: 'Jardin Orbital', description: "Culture en orbite terrestre",
    resource: 'food',
    baseCost: { gold: 20000, food: 100000, faith: 0, knowledge: 10000 },
    baseProduction: 3750, costMultiplier: 1.20, level: 0, maxLevel: 100,
  },
  // Faith
  {
    id: 'fut_neural_temple', name: 'Temple Neural', description: "Spiritualité connectée à l'esprit collectif",
    resource: 'faith',
    baseCost: { gold: 1500, food: 1000, faith: 0, knowledge: 0 },
    baseProduction: 150, costMultiplier: 1.15, level: 0, maxLevel: 100,
  },
  {
    id: 'fut_consciousness_hub', name: 'Hub de Conscience', description: "Convergence des consciences numérisées",
    resource: 'faith',
    baseCost: { gold: 10000, food: 10000, faith: 10000, knowledge: 0 },
    baseProduction: 750, costMultiplier: 1.18, level: 0, maxLevel: 100,
  },
  {
    id: 'fut_cosmic_oracle', name: 'Oracle Cosmique', description: "Sagesse des dimensions parallèles",
    resource: 'faith',
    baseCost: { gold: 50000, food: 30000, faith: 100000, knowledge: 20000 },
    baseProduction: 3750, costMultiplier: 1.20, level: 0, maxLevel: 100,
  },
  // Knowledge
  {
    id: 'fut_quantum_computer', name: 'Ordinateur Quantique', description: "Calculs à puissance quantique",
    resource: 'knowledge',
    baseCost: { gold: 2000, food: 1500, faith: 0, knowledge: 0 },
    baseProduction: 150, costMultiplier: 1.15, level: 0, maxLevel: 100,
  },
  {
    id: 'fut_singularity_engine', name: 'Moteur de Singularité', description: "Dépasse les limites de la connaissance",
    resource: 'knowledge',
    baseCost: { gold: 15000, food: 10000, faith: 5000, knowledge: 10000 },
    baseProduction: 750, costMultiplier: 1.18, level: 0, maxLevel: 100,
  },
  {
    id: 'fut_nexus_archive', name: 'Archive Nexus', description: "Connaissance de toutes les civilisations",
    resource: 'knowledge',
    baseCost: { gold: 50000, food: 30000, faith: 20000, knowledge: 100000 },
    baseProduction: 3750, costMultiplier: 1.20, level: 0, maxLevel: 100,
  },
];
